import { createApp } from "vue";
import { Amplify } from "aws-amplify";
import { sessionStorage } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { useRegisterSW } from "virtual:pwa-register/vue";
import { configConsoleLogLevel } from "./config/logger.js";
import { useCorrelationId } from "./composables/useCorrelationId.js";

configConsoleLogLevel("INFO");

// Print version info to console - dynamic in development, static in production
const printVersionInfo = async () => {
  let version = __APP_VERSION__;
  let commitHash = __COMMIT_HASH__;

  if (import.meta.env.DEV) {
    try {
      const response = await fetch("/api/version");
      const data = await response.json();
      version = data.version;
      commitHash = data.commitHash;
    } catch (error) {
      // Fallback to build-time constants if API fails
    }
  }

  const basePath = import.meta.env.BASE_URL;
  const prMatch = basePath.match(/\/pr-(\d+)\//);
  const prNumber = prMatch ? prMatch[1] : null;

  console.log(
    `

%c %c %cAxial3D Axcel
%cVersion: ${version}
%cCommit: ${commitHash}
${prNumber ? `%c🔬 PR Preview #${prNumber}` : ""}
`,

    "background: linear-gradient(-54.21deg, #102A4F 0%, #102A4F 39.00%, #00A3A0 40.00%, #00A3A0 59.00%, #102A4F 60.00%);border-radius: 0px;vertical-align: middle;font-size: 2.0em;padding: 0 10px;display: inline-block;width: 30px;height: 30px;line-height: 30px;text-align: center;",
    "background: none;",
    "color: #888; font-size: 2.0em; vertical-align: middle;",
    "color: #666; font-size: 1.5em;",
    "color: #666; font-size: 1.2em;",
    ...(prNumber
      ? ["color: #00A3A0; font-size: 1.3em; font-weight: bold;"]
      : []),
  );

  if (prNumber) {
    console.log(
      `%c→ View PR: https://bitbucket.org/axial3d/axcel-webapp/pull-requests/${prNumber}`,
      "color: #00A3A0; font-size: 1.0em;",
    );
  }
};

printVersionInfo();
import {
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signOut,
} from "aws-amplify/auth";
import {
  init,
  browserTracingIntegration,
  replayIntegration,
  withScope,
  captureException,
} from "@sentry/vue";
import RouterApp from "./RouterApp.vue";
import router from "./router";
import { A3dUi } from "@axial3d/ui-components";
import { getApiBaseUrl, getEnvironment } from "@/http/client.js";
import {
  setGPUContext,
  setBrowserContext,
  initRageClickTracking,
  initAutoTracking,
  setCorrelationId,
} from "@/utils/sentry";
import "@axial3d/ui-components/style.css";
import "./assets/main.css";
import { OpfsStorage } from "./caching/storage/OpfsStorage.js";
import type { ServiceWorkerCacheConfig } from "./types/cache.js";
import { SERVICE_WORKER_CONFIG_FILE_NAME } from "./constants/serviceWorkers.js";
import { GATEWAY_CACHE_NAME, GATEWAY_CACHE_VERSION } from "./config/cache.js";
import { fileCacheWorker } from "./workers/index.js";

const amplifyConfig = await fetch(
  `${import.meta.env.BASE_URL}public-aws-exports.json`,
)
  .then((response) => response.json())
  .catch(() => undefined);
if (amplifyConfig) {
  Amplify.configure(amplifyConfig);
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get("token");
  if (urlToken) {
    await sessionStorage.setItem("token", urlToken);
    params.delete("token");
    window.location.search = params.toString();
  }
  const token = await sessionStorage.getItem("token");
  if (token) {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
    try {
      const tokenDetails = JSON.parse(atob(token));
      const session = await fetchAuthSession();
      let loggedIn = session.tokens?.idToken;
      if (loggedIn && loggedIn.payload.email != tokenDetails.username) {
        await signOut();
        loggedIn = undefined;
      }
      if (!loggedIn) {
        const response = await signIn({
          username: tokenDetails.username,
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });
        if (response.nextStep.signInStep) {
          await confirmSignIn({
            challengeResponse: tokenDetails.code,
          });
        }
      }
    } catch (error) {
      console.error("Login error", error);
    }
  }
}

const app = createApp(RouterApp);

const { generateNewCorrelationId } = useCorrelationId();
const correlationId = generateNewCorrelationId();

const environment = getEnvironment();

const SENTRY_PROD_REPLAY_SAMPLE_RATE = 1.0;
const SENTRY_PROD_REPLAY_ON_ERROR_SAMPLE_RATE = 1.0;

if (environment) {
  init({
    app,
    dsn: "https://8d705429b351caac8232cd93e660a2db@o138802.ingest.us.sentry.io/4510459463794688",
    release: `axcel-webapp@${__APP_VERSION__}`,
    integrations: [browserTracingIntegration({ router }), replayIntegration()],
    environment,
    enableLogs: true,
    replaysSessionSampleRate: SENTRY_PROD_REPLAY_SAMPLE_RATE,
    replaysOnErrorSampleRate: SENTRY_PROD_REPLAY_ON_ERROR_SAMPLE_RATE,
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/([a-z0-9]+[.])*axial3d\.com/, /^\//],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      "AbortError",
    ],
    beforeSend(event) {
      event.contexts = {
        ...event.contexts,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
        },
      };
      return event;
    },
  });

  setGPUContext();
  setBrowserContext();
  initRageClickTracking();
  setCorrelationId(correlationId);
  initAutoTracking(router);

  app.config.errorHandler = (err, instance, info) => {
    withScope((scope) => {
      scope.setContext("vue", {
        componentName: instance?.$options?.name || "Unknown",
        errorInfo: info,
        props: instance?.$props,
      });
      captureException(err);
    });
  };

  window.addEventListener("unhandledrejection", (event) => {
    withScope((scope) => {
      scope.setTag("type", "unhandledrejection");
      captureException(event.reason);
    });
  });
}

app.use(router);
app.use(A3dUi);

app.mount("#app");

const fileSystemDirectoryHandle = await navigator.storage.getDirectory();
const opfsStorage = new OpfsStorage(fileSystemDirectoryHandle);

const textEncoder = new TextEncoder();

const serviceWorkerCacheConfig: ServiceWorkerCacheConfig = {
  apiBaseUrl: getApiBaseUrl(),
  cacheName: GATEWAY_CACHE_NAME,
  cacheVersion: GATEWAY_CACHE_VERSION,
};

await opfsStorage.saveData(
  textEncoder.encode(JSON.stringify(serviceWorkerCacheConfig)).buffer,
  SERVICE_WORKER_CONFIG_FILE_NAME,
);

useRegisterSW({
  immediate: true,
  onRegisteredSW(swUrl) {
    console.log(`Service Worker registered at: ${swUrl}`);
  },
  onRegisterError(error) {
    console.error("Service Worker registration failed:", error);
  },
});

fileCacheWorker.onMessage<"WRITE_CACHE">((event) => {
  const { executionTime, fileName, fileSize } = event.data.payload;

  console.log(
    `${fileName} with size: ${fileSize} successfully written to storage by worker in ${executionTime}`,
  );
});
